/** RaiderMapCR - Campus directory with faculty search and reviews */
import React, { useState, useEffect, useCallback } from 'react';
import { flushSync } from 'react-dom';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Rating,
  Alert,
  Slider
} from '@mui/material';
import { 
  ContactPhone as DirectoryIcon, 
  Person as PersonIcon, 
  Business as DepartmentIcon,
  Search as SearchIcon,
  Email as EmailIcon,
  FilterList as FilterListIcon,
  Star as StarIcon,
  Close as CloseIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';

/** RaiderMapCR - Review display for faculty */
const ReviewDisplay = ({ facultyId, showAll = false, hideStats = false, onStatsLoaded }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(showAll);
  const [stats, setStats] = useState({
    average_rating: 0,
    average_grade: 0,
    total_reviews: 0
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/reviews?faculty_id=${facultyId}`);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to fetch reviews');
        }

        setReviews(data.reviews || []);
        const newStats = {
          average_rating: data.average_rating || 0,
          average_grade: data.average_grade || 0,
          total_reviews: data.total_reviews || 0
        };
        setStats(newStats);
        if (onStatsLoaded) {
          onStatsLoaded(newStats);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (facultyId) {
      fetchData();
    }
  }, [facultyId, onStatsLoaded]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getGradeColor = (grade) => {
    if (grade >= 90) return '#4CAF50';
    if (grade >= 80) return '#8BC34A';
    if (grade >= 70) return '#FFC107';
    if (grade >= 60) return '#FF9800';
    return '#F44336';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress sx={{ color: '#AA3333' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2, backgroundColor: 'rgba(211, 47, 47, 0.1)' }}>
        {error}
      </Alert>
    );
  }

  if (stats.total_reviews === 0) {
    return (
      <Box sx={{ textAlign: 'center', p: 3 }}>
        <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          No reviews yet. Be the first to review this faculty member!
        </Typography>
      </Box>
    );
  }

  const displayReviews = expanded ? reviews : reviews.slice(0, 3);

  return (
    <Box>
      {!hideStats && (
      <Box sx={{ 
        mb: 3, 
        p: 2, 
        backgroundColor: 'rgba(42, 42, 42, 0.75)',
        border: '1px solid rgba(74, 74, 74, 0.6)',
        backdropFilter: 'blur(12px)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        borderRadius: 3 
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <StarIcon sx={{ color: '#AA3333', mr: 1 }} />
          <Typography variant="h6" sx={{ color: '#ffffff' }}>
            Reviews ({stats.total_reviews})
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {stats.total_reviews >= 3 ? (
            <>
              <Rating
                value={stats.average_rating}
                readOnly
                precision={0.1}
                sx={{
                  '& .MuiRating-iconFilled': {
                    color: '#AA3333',
                  },
                }}
              />
              <Typography variant="body1" sx={{ color: '#ffffff', ml: 1 }}>
                {stats.average_rating.toFixed(1)}/5
              </Typography>
            </>
          ) : (
            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              N/A
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mr: 1 }}>
            Average Grade:
          </Typography>
          <Chip
            label={`${stats.average_grade.toFixed(1)}%`}
            size="small"
            sx={{
              backgroundColor: getGradeColor(stats.average_grade),
              color: '#ffffff',
              fontWeight: 'bold'
            }}
          />
        </Box>
      </Box>
      )}
      <Box>
        {displayReviews.map((review) => (
          <Card
            key={review.id}
            sx={{
              mb: 2,
              backgroundColor: 'rgba(42, 42, 42, 0.75)',
              border: '1px solid rgba(74, 74, 74, 0.6)',
              backdropFilter: 'blur(12px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              borderRadius: 3
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PersonIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', mr: 1, fontSize: 24, verticalAlign: 'middle' }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Student
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  {formatDate(review.created_at)}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Rating
                  value={review.rating}
                  readOnly
                  size="small"
                  sx={{
                    '& .MuiRating-iconFilled': {
                      color: '#AA3333',
                    },
                  }}
                />
                <Chip
                  label={`${review.grade}%`}
                  size="small"
                  sx={{
                    ml: 1,
                    backgroundColor: getGradeColor(review.grade),
                    color: '#ffffff',
                    fontWeight: 'bold'
                  }}
                />
              </Box>

              {review.comment && (
                <Typography variant="body2" sx={{ color: '#ffffff', mt: 1 }}>
                  {review.comment}
                </Typography>
              )}
            </CardContent>
          </Card>
        ))}
        {reviews.length > 3 && (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Button
              onClick={() => setExpanded(!expanded)}
              endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{
                color: '#AA3333',
                '&:hover': {
                  backgroundColor: 'rgba(170, 51, 51, 0.1)'
                }
              }}
            >
              {expanded ? 'Show Less' : `Show All ${reviews.length} Reviews`}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

/** RaiderMapCR - Review submission modal */
const ReviewModal = ({ open, onClose, facultyMember, onReviewSubmitted }) => {
  const [formData, setFormData] = useState({
    email: '',
    rating: 5,
    grade: 85,
    comment: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleClose = () => {
    setFormData({
      email: '',
      rating: 5,
      grade: 85,
      comment: ''
    });
    setError('');
    setSuccess('');
    onClose();
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/reviews', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          faculty_id: facultyMember.id,
          email: formData.email,
          rating: formData.rating,
          grade: formData.grade,
          comment: formData.comment
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit review');
      }

      setSuccess(data.message);
      if (onReviewSubmitted) {
        onReviewSubmitted();
      }

      setTimeout(() => {
        handleClose();
      }, 2000);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!facultyMember) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(12px)'
        }
      }}
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(42, 42, 42, 0.75)',
          border: '1px solid rgba(74, 74, 74, 0.6)',
          backdropFilter: 'blur(12px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          color: '#ffffff',
          borderRadius: 3
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        color: '#ffffff',
        borderBottom: '1px solid rgba(74, 74, 74, 0.6)'
      }}>
        <Typography variant="h6">
          Review {facultyMember.name}
        </Typography>
        <Button
          onClick={handleClose}
          sx={{ 
            minWidth: 'auto', 
            p: 1,
            color: '#ffffff',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, backgroundColor: 'rgba(211, 47, 47, 0.1)' }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2, backgroundColor: 'rgba(76, 175, 80, 0.1)' }}>
            {success}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Box sx={{ mb: 3, p: 2, backgroundColor: 'rgba(74, 74, 74, 0.3)', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ color: '#ffffff', mb: 1 }}>
              {facultyMember.name}
            </Typography>
            {facultyMember.title && (
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                {facultyMember.title}
              </Typography>
            )}
            {facultyMember.department && (
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                {facultyMember.department}
              </Typography>
            )}
          </Box>
          <TextField
            fullWidth
            label="Your Email (@ttu.edu)"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            required
            disabled={loading}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                color: '#ffffff',
                '& fieldset': {
                  borderColor: 'rgba(74, 74, 74, 0.6)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#AA3333',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
                '&.Mui-focused': {
                  color: '#AA3333',
                },
              },
            }}
          />
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ color: '#ffffff', mb: 1 }}>
              Overall Rating (1-5 stars)
            </Typography>
            <Rating
              value={formData.rating}
              onChange={(e, newValue) => handleInputChange('rating', newValue)}
              disabled={loading}
              sx={{
                '& .MuiRating-iconFilled': {
                  color: '#AA3333',
                },
                '& .MuiRating-iconHover': {
                  color: '#AA3333',
                },
              }}
            />
          </Box>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ color: '#ffffff', mb: 2 }}>
              Grade Received: {formData.grade}%
            </Typography>
            <Slider
              value={formData.grade}
              onChange={(e, newValue) => handleInputChange('grade', newValue)}
              min={1}
              max={100}
              step={1}
              disabled={loading}
              sx={{
                color: '#AA3333',
                '& .MuiSlider-thumb': {
                  '&:hover, &.Mui-focusVisible': {
                    boxShadow: '0 0 0 8px rgba(170, 51, 51, 0.16)',
                  },
                },
                '& .MuiSlider-track': {
                  backgroundColor: '#AA3333',
                },
                '& .MuiSlider-rail': {
                  backgroundColor: 'rgba(74, 74, 74, 0.6)',
                },
              }}
            />
          </Box>
          <TextField
            fullWidth
            label="Comments (Optional)"
            value={formData.comment}
            onChange={(e) => handleInputChange('comment', e.target.value)}
            multiline
            rows={4}
            disabled={loading}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: '#ffffff',
                '& fieldset': {
                  borderColor: 'rgba(74, 74, 74, 0.6)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#AA3333',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
                '&.Mui-focused': {
                  color: '#AA3333',
                },
              },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(74, 74, 74, 0.6)' }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{ 
            color: 'rgba(255, 255, 255, 0.7)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading || !formData.email || !formData.email.endsWith('@ttu.edu')}
          variant="contained"
          sx={{
            backgroundColor: '#AA3333',
            '&:hover': {
              backgroundColor: '#8B2A2A',
            },
            '&:disabled': {
              backgroundColor: 'rgba(74, 74, 74, 0.6)',
              color: 'rgba(255, 255, 255, 0.3)',
            },
          }}
        >
          {loading ? (
            <CircularProgress size={20} sx={{ color: '#ffffff' }} />
          ) : (
            'Submit Review'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** RaiderMapCR - Faculty profile modal with reviews */
const FacultyProfileModal = ({ open, onClose, facultyMember }) => {
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewStats, setReviewStats] = useState(null);

  const handleClose = () => {
    onClose();
  };

  const handleReviewSubmitted = () => {
    // ReviewDisplay will refetch when mounted; no-op here
  };

  if (!facultyMember) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(42, 42, 42, 0.95)',
            border: '1px solid rgba(74, 74, 74, 0.6)',
            backdropFilter: 'blur(12px)',
            color: '#ffffff',
            maxHeight: '90vh',
            minHeight: '56vh',
            borderRadius: 3,
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        color: '#ffffff',
        borderBottom: '1px solid rgba(74, 74, 74, 0.6)',
        pb: 2,
        position: 'sticky',
        top: 0,
        zIndex: 2,
        backgroundColor: 'rgba(42, 42, 42, 0.95)'
      }}>
          <Box>
            <Typography variant="h5" sx={{ color: '#ffffff', mb: 0.5 }}>
              {facultyMember.name}
            </Typography>
            {facultyMember.title && (
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                {facultyMember.title}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{ 
              color: '#ffffff',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, overflowY: 'auto', flex: 1 }}>
          <Box sx={{ p: 0, pl: 2, borderBottom: '1px solid rgba(74, 74, 74, 0.6)' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, py: 1.5 }}>
              {facultyMember.department && (
                <Chip
                  icon={<DepartmentIcon sx={{ fontSize: 20 }} />}
                  label={facultyMember.department}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: 'rgba(74, 74, 74, 0.6)',
                    color: '#ffffff',
                    height: 'auto',
                    minHeight: 26,
                    maxWidth: 320,
                    alignItems: 'center',
                    flexWrap: 'nowrap',
                    '& .MuiChip-label': {
                      fontSize: '0.8rem',
                      px: 1.25,
                      whiteSpace: 'normal !important',
                      textOverflow: 'clip',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      wordBreak: 'break-word',
                      minWidth: 0
                    },
                    '& .MuiChip-icon': {
                      color: '#ffffff',
                      alignSelf: 'center',
                      marginLeft: '8px'
                    }
                  }}
                />
              )}
              {facultyMember.email && (
                <Chip
                  icon={<EmailIcon sx={{ fontSize: 20 }} />}
                  label={facultyMember.email}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: 'rgba(74, 74, 74, 0.6)',
                    color: '#ffffff',
                    height: 26,
                    alignItems: 'center',
                    flexWrap: 'nowrap',
                    '& .MuiChip-label': {
                      fontSize: '0.8rem',
                      px: 1.25
                    },
                    '& .MuiChip-icon': {
                      color: '#ffffff',
                      alignSelf: 'center',
                      marginLeft: '8px'
                    }
                  }}
                />
              )}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

              {facultyMember.phone && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PhoneIcon sx={{ mr: 1, fontSize: 20, color: 'rgba(255, 255, 255, 0.7)' }} />
                  <Typography variant="body2" sx={{ color: '#ffffff' }}>
                    {facultyMember.phone}
                  </Typography>
                </Box>
              )}

              {facultyMember.office && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocationIcon sx={{ mr: 1, fontSize: 20, color: 'rgba(255, 255, 255, 0.7)' }} />
                  <Typography variant="body2" sx={{ color: '#ffffff' }}>
                    Office: {facultyMember.office}
                  </Typography>
                </Box>
              )}

              {facultyMember.hours && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TimeIcon sx={{ mr: 1, fontSize: 20, color: 'rgba(255, 255, 255, 0.7)' }} />
                  <Typography variant="body2" sx={{ color: '#ffffff' }}>
                    Hours: {facultyMember.hours}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6" sx={{ color: '#ffffff' }}>
                  Reviews
                </Typography>
                {reviewStats && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 24 }}>
                      {reviewStats.total_reviews >= 3 ? (
                        <>
                          <Rating
                            value={reviewStats.average_rating}
                            readOnly
                            precision={0.1}
                            sx={{ '& .MuiRating-iconFilled': { color: '#AA3333' } }}
                          />
                          <Typography variant="body1" sx={{ color: '#ffffff', ml: 1 }}>
                            {reviewStats.average_rating.toFixed(1)}/5
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          N/A
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mr: 1 }}>
                        Average Grade:
                      </Typography>
                      <Chip
                        label={`${reviewStats.average_grade.toFixed(1)}%`}
                        size="small"
                        sx={{
                          backgroundColor: reviewStats.average_grade >= 90 ? '#4CAF50' : reviewStats.average_grade >= 80 ? '#8BC34A' : reviewStats.average_grade >= 70 ? '#FFC107' : reviewStats.average_grade >= 60 ? '#FF9800' : '#F44336',
                          color: '#ffffff',
                          fontWeight: 'bold'
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
              <Button
                variant="contained"
                onClick={() => setReviewModalOpen(true)}
                sx={{
                  backgroundColor: '#AA3333',
                  '&:hover': {
                    backgroundColor: '#8B2A2A',
                  },
                }}
              >
                Submit a Review
              </Button>
            </Box>
            <ReviewDisplay 
              facultyId={facultyMember.id} 
              showAll={true} 
              hideStats={true}
              onStatsLoaded={setReviewStats}
            />
          </Box>
        </DialogContent>
      </Dialog>
      <ReviewModal
        open={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        facultyMember={facultyMember}
        onReviewSubmitted={handleReviewSubmitted}
      />
    </>
  );
};

const DirectoryPage = () => {
  const [directory, setDirectory] = useState([]);
  const [filteredDirectory, setFilteredDirectory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [filters, setFilters] = useState({
    department: ''
  });
  const [selectedFaculty, setSelectedFaculty] = useState(null);
  const [profileModalOpen, setProfileModalOpen] = useState(false);

  useEffect(() => {
    fetch('/api/admin/directory')
      .then(response => response.json())
      .then(data => {
        setDirectory(data);
        setFilteredDirectory(data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching directory:', error);
        setLoading(false);
      });
    const urlParams = new URLSearchParams(window.location.search);
    const searchParam = urlParams.get('search');
    if (searchParam) {
      setSearchTerm(searchParam);
    }
  }, []);

  useEffect(() => {
    const filtered = directory.filter(person => {
      // Search term filtering
      const matchesSearch = person.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (person.department && person.department.toLowerCase().includes(searchTerm.toLowerCase())) ||
                           (person.title && person.title.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesDepartment = !filters.department || 
                               (person.department && person.department.toLowerCase() === filters.department.toLowerCase());

      return matchesSearch && matchesDepartment;
    });
    setFilteredDirectory(filtered);
  }, [searchTerm, directory, filters]);

  const handleFilterClick = useCallback(() => {
    if (filterPanelOpen) {
      flushSync(() => {
        setIsAnimating(true);
        setFilterPanelOpen(false);
      });
      setTimeout(() => {
        setIsAnimating(false);
      }, 200);
    } else {
      flushSync(() => {
        setIsAnimating(true);
        setFilterPanelOpen(true);
      });
    }
  }, [filterPanelOpen]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleFacultyClick = (faculty) => {
    setSelectedFaculty(faculty);
    setProfileModalOpen(true);
  };

  const handleCloseProfile = () => {
    setProfileModalOpen(false);
    setSelectedFaculty(null);
  };

  const getUniqueDepartments = () => {
    return [...new Set(directory.map(person => person.department).filter(Boolean))];
  };

  if (loading) {
    return (
      <div className="main-content">
        <Container>
          <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
            <CircularProgress />
          </Box>
        </Container>
      </div>
    );
  }

  return (
    <div className="main-content">
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <DirectoryIcon sx={{ fontSize: 64, mb: 2, color: 'primary.main' }} />
          <Typography variant="h3" component="h1" gutterBottom sx={{ color: '#ffffff' }}>
            Campus Directory
          </Typography>
          <Typography variant="h6" sx={{ color: '#ffffff' }}>
            Find faculty, staff, and services
          </Typography>
        </Box>

        <Box sx={{ mb: 4, position: 'relative', zIndex: 10 }}>
          <Box sx={{ position: 'relative' }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Name, department, or title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoComplete="off"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#ffffff', fontSize: 24 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleFilterClick}
                      sx={{ 
                        color: '#ffffff',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        }
                      }}
                    >
                      <FilterListIcon sx={{ fontSize: 24 }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  borderRadius: (filterPanelOpen || isAnimating)
                    ? { xs: '12px 12px 0 0', md: '12px 12px 0 12px' }
                    : 3,
                  backgroundColor: 'rgba(42, 42, 42, 0.85)',
                  border: '1px solid rgba(74, 74, 74, 0.6)',
                  backdropFilter: 'blur(12px)',
                  height: '56px',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#ffffff',
                  transition: 'border-radius 150ms ease-in-out',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderWidth: 2,
                      borderColor: 'primary.main',
                    },
                  },
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderRadius: (filterPanelOpen || isAnimating)
                    ? { xs: '12px 12px 0 0', md: '12px 12px 0 12px' }
                    : 3,
                  borderWidth: '1px',
                  height: '68px',
                  top: '-12px',
                  border: 'none',
                  transition: 'border-radius 150ms ease-in-out',
                },
                '& .MuiInputBase-input': {
                  padding: '16.5px 14px',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#ffffff',
                  '&::placeholder': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                },
                '& .MuiInputAdornment-root': {
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#ffffff',
                },
              }}
            />
            <Box sx={{ 
              position: 'absolute',
              top: '100%',
              left: { xs: 0, md: 'auto' },
              right: 0,
              width: { xs: '100%', md: '450px' },
              maxWidth: { xs: '100%', md: '450px' },
              zIndex: 5,
              overflow: 'hidden',
              maxHeight: filterPanelOpen ? '1000px' : '0px',
              transition: 'max-height 300ms ease-in-out',
              borderBottomLeftRadius: 12,
              borderBottomRightRadius: 12,
            }}>
              <Box sx={{ 
                p: 3,
                backgroundColor: 'rgba(42, 42, 42, 0.85)',
                border: '1px solid rgba(74, 74, 74, 0.6)',
                borderTop: 'none',
                borderBottomLeftRadius: 12,
                borderBottomRightRadius: 12,
                backdropFilter: 'blur(12px)',
              }}>
              <Grid container spacing={2}>
                {/* Department Filter */}
                <Grid item xs={12}>
                  <FormControl component="fieldset" sx={{ width: '100%' }}>
                    <FormLabel 
                      component="legend" 
                      sx={{ 
                        color: '#ffffff !important', 
                        fontWeight: 'bold',
                        mb: 1,
                        fontSize: '0.8rem',
                        pointerEvents: 'none',
                        cursor: 'default',
                        '&.Mui-focused': {
                          color: '#ffffff !important'
                        }
                      }}
                    >
                      Department
                    </FormLabel>
                    <RadioGroup
                      value={filters.department}
                      onChange={(e) => handleFilterChange('department', e.target.value)}
                      sx={{ 
                        '& .MuiFormControlLabel-label': {
                          color: '#ffffff',
                          fontSize: '0.75rem'
                        },
                        '& .MuiRadio-root': {
                          color: 'rgba(255, 255, 255, 0.7)',
                          padding: '2px',
                          '&.Mui-checked': {
                            color: '#AA3333'
                          }
                        }
                      }}
                    >
                      <FormControlLabel 
                        value="" 
                        control={<Radio size="small" />} 
                        label="All" 
                      />
                      {getUniqueDepartments().map(department => (
                        <FormControlLabel 
                          key={department}
                          value={department} 
                          control={<Radio size="small" />} 
                          label={department} 
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </Grid>
              </Grid>
              </Box>
            </Box>
          </Box>
        </Box>

        <Grid container spacing={3} justifyContent="center">
          {filteredDirectory.length === 0 ? (
            <Grid item xs={12}>
              <Card sx={{
                backgroundColor: 'rgba(42, 42, 42, 0.85)',
                border: '1px solid rgba(74, 74, 74, 0.6)',
                backdropFilter: 'blur(12px)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
              }}>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" sx={{ color: '#ffffff' }}>
                    {searchTerm ? 'No results found for your search.' : 'Directory information coming soon...'}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, color: '#ffffff' }}>
                    {searchTerm ? 'Directory information coming soon...' : 'We\'re building the comprehensive campus directory.'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ) : (
            filteredDirectory.map((person, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ 
                  height: '100%',
                  display: 'flex',
                  backgroundColor: 'rgba(42, 42, 42, 0.85)',
                  border: '1px solid rgba(74, 74, 74, 0.6)',
                  backdropFilter: 'blur(12px)',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
                  transition: 'all 0.3s ease-in-out',
                  cursor: 'pointer',
                  alignItems: 'center',
                  '&:hover': {
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    borderColor: 'rgba(170, 51, 51, 0.5)',
                  }
                }}
                onClick={() => handleFacultyClick(person)}>
                  <CardContent sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.5,
                    height: '100%',
                    width: '100%',
                    textAlign: 'center',
                    py: 3,
                    px: 2,
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                      <PersonIcon sx={{ mr: 1, fontSize: 28, color: '#ffffff', display: { xs: 'none', md: 'inline' } }} />
                      <Typography variant="h6" component="h2" sx={{ color: '#ffffff' }}>
                        {person.name}
                      </Typography>
                    </Box>

                     {person.department && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                         <Chip 
                           icon={<DepartmentIcon sx={{ fontSize: 16 }} />}
                           label={person.department}
                           size="small" 
                           variant="outlined"
                           sx={{ 
                             display: 'flex',
                             justifyContent: 'center',
                             borderColor: 'rgba(74, 74, 74, 0.6)',
                             color: '#ffffff',
                             height: 'auto',
                             minHeight: 26,
                             maxWidth: 270,
                             alignItems: 'center',
                             flexWrap: 'nowrap',
                             '& .MuiChip-label': {
                               whiteSpace: 'normal !important',
                               textOverflow: 'clip',
                               display: '-webkit-box',
                               WebkitLineClamp: 2,
                               WebkitBoxOrient: 'vertical',
                               overflow: 'hidden',
                               wordBreak: 'break-word',
                               minWidth: 0
                             },
                             '& .MuiChip-icon': {
                                marginLeft: '7px',
                                alignSelf: 'center'
                             }
                           }}
                         />
                       </Box>
                     )}

                     {person.email && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                         <Chip 
                           icon={<EmailIcon sx={{ fontSize: 16 }} />}
                           label={person.email}
                           size="small" 
                           variant="outlined"
                           sx={{ 
                             display: 'flex', 
                             alignItems: 'center', 
                             justifyContent: 'center',
                             borderColor: 'rgba(74, 74, 74, 0.6)',
                             color: '#ffffff',
                         flexWrap: 'nowrap',
                             '& .MuiChip-icon': {
                                marginLeft: '8px',
                           alignSelf: 'center'
                             }
                           }}
                         />
                       </Box>
                     )}

                    {/* Review Button */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center',
                      alignItems: 'center',
                      mt: 0.5
                    }}>
                      <Chip
                        icon={<StarIcon sx={{ fontSize: 20 }} />}
                        label="Reviews"
                        size="small"
                        clickable
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFacultyClick(person);
                        }}
                        sx={{
                          backgroundColor: 'rgba(170, 51, 51, 0.2)',
                          border: '1px solid rgba(170, 51, 51, 0.5)',
                          color: '#AA3333',
                          '&:hover': {
                            backgroundColor: 'rgba(170, 51, 51, 0.3)',
                            borderColor: '#AA3333',
                          },
                          '& .MuiChip-icon': {
                            color: '#AA3333'
                          }
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      </Container>
      <FacultyProfileModal
        open={profileModalOpen}
        onClose={handleCloseProfile}
        facultyMember={selectedFaculty}
      />
    </div>
  );
};

export default DirectoryPage;
